import { CircularProgress, Dialog, DialogContent, DialogContentText, DialogTitle } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { telemetry } from "telemetry";

import { activateAndReload, findNewerVersion } from "./requiredUpdate";

/** Blocks the app while it updates itself to the latest deployed version. */
export function RequiredUpdateDialog({
    registration,
    registrationFailed,
}: {
    registration: ServiceWorkerRegistration | undefined;
    registrationFailed: boolean;
}) {
    const { t } = useTranslation();
    const [noUpdate, setNoUpdate] = useState(false);
    const unavailable = noUpdate || registrationFailed;

    useEffect(() => {
        // Sent long before any reload, so it reliably counts affected clients.
        telemetry.trackEvent("app.required_update", { "update.result": "required" });
    }, []);

    useEffect(() => {
        if (!registrationFailed) {
            return;
        }
        telemetry.trackEvent("app.required_update", {
            "update.result": "unavailable",
            "update.reason": "registration_failed",
        });
    }, [registrationFailed]);

    useEffect(() => {
        if (!registration) {
            return;
        }

        let cancelled = false;
        void findNewerVersion(registration).then((waiting) => {
            if (cancelled) {
                return;
            }
            if (waiting) {
                telemetry.trackEvent("app.required_update", { "update.result": "updating" });
                activateAndReload(waiting);
            } else {
                telemetry.trackEvent("app.required_update", {
                    "update.result": "unavailable",
                    "update.reason": "no_update",
                });
                setNoUpdate(true);
            }
        });
        return () => {
            cancelled = true;
        };
    }, [registration]);

    return (
        <Dialog open={true} disableEscapeKeyDown>
            <DialogTitle>{t("updateRequired")}</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    {unavailable ? t("updateRequiredUnavailable") : t("updateRequiredDescription")}
                </DialogContentText>
                {!unavailable && <CircularProgress size={24} sx={{ display: "block", mx: "auto", mt: 2 }} />}
            </DialogContent>
        </Dialog>
    );
}
